import * as Sentry from '@sentry/react-router';
import { getCurrentAppVersion } from '@travelwin/core';
import { appEnv } from '@travelwin/core/src/utils/env';

import { getAuthUser } from '../context/User';

const getUserCallback = async () => {
  const { username, userId } = await getAuthUser();
  return {
    id: userId,
    email: username,
  };
};

const allowEvent = (event: Sentry.ErrorEvent) => {
  // Connection errors shouldn't be logged
  if (!navigator.onLine) {
    return false;
  }

  const errors = event.exception?.values;

  const isCompatibilityError = () =>
    errors?.some((e) => e.type === 'ApiCompatibilityError');
  const isIFrameEmbedError = () =>
    errors?.some(
      (e) =>
        e.stacktrace?.frames?.some((f) => f.filename?.includes('embed.js')),
    );

  return !(isCompatibilityError() || isIFrameEmbedError());
};

export const initReactRouterClientSentry = () => {
  if (!appEnv.isVersioned()) return;
  try {
    Sentry.init({
      dsn: 'https://586146b24e0942aebb062b0b7adaf0a9@o4504831327666176.ingest.sentry.io/4504871062405120',
      sampleRate: 1.0,
      sendDefaultPii: true,
      attachStacktrace: true,
      environment: appEnv.getEnv(),
      release: getCurrentAppVersion(),
      beforeSend: async (event: Sentry.ErrorEvent) => {
        try {
          if (!allowEvent(event)) {
            return null;
          }
        } catch {
          const message =
            'Failed to verify if the event was allowed to be sent to Sentry.';
          // By default, we'll return the event if allowEvent fails.
          console.error(message, event);
          const newBreadcrumb: Sentry.Breadcrumb = {
            level: 'warning',
            message,
          };

          event.breadcrumbs?.push(newBreadcrumb);
        }

        try {
          // AWS Auth.config may not have been called yet at this point.
          // In this case, we'll catch and send the event anyways without the user info.
          if (getUserCallback) {
            const user = await getUserCallback();
            event.user = event.user || {};
            event.user.id = user.id;
            event.user.email = user.email;
          }

          return event;
        } catch {
          return event;
        }
      },
    });
  } catch (e) {
    console.error(`React router sentry is misconfigured ${e}`);
  }
};
